/**
 * SignatureLedger — three-slot signature panel for the ePCR (Sprint J).
 *
 * Captures the EMT attestation, the patient (or surrogate) consent /
 * refusal, and the receiving clinician handoff acknowledgement. Each
 * signature row collects a typed name + a drawn signature image plus
 * an explicit attestation string. On capture, the row writes to the
 * `epcr_signatures` table with a SHA-256 hash of (attestation + name
 * + timestamp) so any subsequent change to the underlying record is
 * detectable.
 *
 * The HIPAA per-record access log gets a `sign` event stamped for
 * each captured signature.
 *
 * Once signed, a slot renders in read-only mode showing the captured
 * image, name, time, and tamper-detection hash. Locking the ePCR
 * prevents new signatures and disables clear/cancel.
 */

import { useEffect, useState } from 'react';
import { Check, Loader2, AlertTriangle, ShieldCheck } from 'lucide-react';
import type { EPCR } from '@/types';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/lib/auth-store';
import { logEpcrAccess, sha256Hex } from '@/lib/epcr-audit';
import { SignaturePad } from '@/components/SignaturePad';

type Role = 'emt' | 'patient' | 'receiving_clinician';

const ROLE_META: Record<Role, {
  label: string;
  attestation: string;
  hint: string;
  needsTitle?: boolean;
  defaultTitle?: string;
}> = {
  emt: {
    label: 'EMT attestation',
    attestation:
      'I certify that the foregoing record is a true, accurate, and complete account of the ' +
      'patient care I provided. I attest under penalty of perjury that no entries have been ' +
      'falsified or omitted.',
    hint: 'Required at QA sign-off. Locks your name + timestamp + the attestation copy above.',
    needsTitle: true,
    defaultTitle: 'EMT',
  },
  patient: {
    label: 'Patient signature',
    attestation:
      'I acknowledge that I have received an explanation of the care, treatment, and / or ' +
      'transport options provided by EMS. I have had an opportunity to ask questions and ' +
      'understand the risks of any care or transport I am declining.',
    hint:
      'For consent to treat, refusal of care, refusal of transport, or transport authorization. ' +
      'Surrogate (parent / guardian / POA) may sign on behalf of an incapacitated patient.',
  },
  receiving_clinician: {
    label: 'Receiving clinician handoff',
    attestation:
      'I have received verbal and written report from the EMS crew identified on this record ' +
      'and accept transfer of care for the patient at the time and date indicated.',
    hint:
      'Receiving RN, MD, PA, or NP at the destination facility signs at handoff. ' +
      'NEMSIS eDisposition.30: Person/Agency Caregiver Verifying Information.',
    needsTitle: true,
    defaultTitle: 'RN',
  },
};

interface SignatureRow {
  id: string;
  role: Role;
  signed_name: string;
  signed_title: string | null;
  signed_at: string;
  signed_by_user: string | null;
  attestation: string;
  signature_png: string | null;
  signature_hash: string;
}

interface Props {
  epcr: EPCR;
}

export function SignatureLedger({ epcr }: Props) {
  const [rows, setRows] = useState<SignatureRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Pull existing signatures for this ePCR.
  useEffect(() => {
    let cancel = false;
    void (async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('epcr_signatures')
          .select('*')
          .eq('epcr_id', epcr.id)
          .order('signed_at', { ascending: true });
        if (!cancel && !error) setRows((data ?? []) as SignatureRow[]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [epcr.id]);

  function getByRole(r: Role): SignatureRow | undefined {
    return rows.find((row) => row.role === r);
  }

  async function onSigned(role: Role, row: SignatureRow) {
    setRows((prev) => [...prev.filter((r) => r.role !== role), row]);
  }

  return (
    <section className="space-y-3 border border-slate-700 rounded-lg bg-slate-900/40 p-4">
      <header className="flex items-baseline gap-2 flex-wrap">
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-200">
          Signatures
        </h3>
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
          (HIPAA · NEMSIS eDisposition.30 · legal defensibility)
        </span>
        <span className="ml-auto text-[10px] font-mono text-slate-500">
          {rows.length} of 3
        </span>
      </header>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading signatures…
        </div>
      ) : (
        <div className="space-y-3">
          {(['emt', 'patient', 'receiving_clinician'] as const).map((role) => {
            const existing = getByRole(role);
            return (
              <SignatureSlot
                key={role}
                role={role}
                epcr={epcr}
                existing={existing}
                onSigned={(row) => onSigned(role, row)}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}

function SignatureSlot({
  role, epcr, existing, onSigned,
}: {
  role: Role;
  epcr: EPCR;
  existing: SignatureRow | undefined;
  onSigned: (row: SignatureRow) => void;
}) {
  const meta = ROLE_META[role];
  const { user } = useAuthStore();

  const [signing, setSigning] = useState(false);
  const [name, setName]   = useState(role === 'emt' ? user?.full_name ?? '' : '');
  const [title, setTitle] = useState<string>(role === 'emt' ? user?.cert_level ?? '' : (meta.defaultTitle ?? ''));
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingPng, setPendingPng] = useState<string | null>(null);

  if (existing) {
    return <SignedView row={existing} />;
  }

  if (!signing) {
    return (
      <div className="border border-dashed border-slate-600 rounded p-3 flex items-center justify-between gap-3 flex-wrap bg-slate-950/40">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-200 text-sm">{meta.label}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{meta.hint}</div>
        </div>
        <button
          type="button"
          onClick={() => setSigning(true)}
          disabled={epcr.locked}
          className="rounded-md bg-blue-700 hover:bg-blue-800 disabled:bg-slate-700 disabled:cursor-not-allowed px-3 py-2 text-sm font-semibold text-white min-h-touch"
        >
          Capture signature
        </button>
      </div>
    );
  }

  async function commit() {
    setError(null);
    if (!name.trim()) {
      setError('Typed name is required.');
      return;
    }
    if (!pendingPng) {
      setError('Please draw a signature in the pad before saving.');
      return;
    }
    setBusy(true);
    try {
      const signed_at = new Date().toISOString();
      const hash = await sha256Hex(`${meta.attestation}|${name}|${signed_at}`);
      const id = crypto.randomUUID();
      const row: SignatureRow = {
        id,
        role,
        signed_name: name.trim(),
        signed_title: title.trim() || null,
        signed_at,
        signed_by_user: role === 'emt' ? user?.id ?? null : null,
        attestation: meta.attestation,
        signature_png: pendingPng,
        signature_hash: hash,
      };
      // Persist to backend. (RLS allows authenticated insert.)
      const { error: insertErr } = await supabase
        .from('epcr_signatures')
        .insert({
          id,
          epcr_id: epcr.id,
          agency_id: user?.agency_id ?? null,
          role,
          signed_name: row.signed_name,
          signed_title: row.signed_title,
          signed_at,
          signed_by_user: row.signed_by_user,
          attestation: row.attestation,
          signature_png: pendingPng,
          signature_hash: hash,
        });
      if (insertErr) {
        // Most likely cause: dev mode without backend, or the migration
        // hasn't been run yet. Don't lose the user's input — surface
        // the message instead of silently failing.
        throw insertErr;
      }
      logEpcrAccess(epcr.id, 'sign', { role, signed_name: row.signed_name });
      onSigned(row);
      setSigning(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record signature.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border border-blue-700 rounded p-3 bg-slate-950/60 space-y-3">
      <div>
        <div className="font-semibold text-slate-200 text-sm">{meta.label}</div>
        <p className="text-[11px] text-slate-300 mt-1 leading-snug italic">
          &ldquo;{meta.attestation}&rdquo;
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
            Typed name *
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 min-h-touch focus:border-blue-500 focus:outline-none"
            autoComplete="name"
          />
        </label>
        {meta.needsTitle && (
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
              Title (RN, MD, EMT, etc.)
            </span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full rounded border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 min-h-touch focus:border-blue-500 focus:outline-none"
            />
          </label>
        )}
      </div>

      <SignaturePad
        height={140}
        label="Sign in the box"
        onCapture={(dataUrl) => setPendingPng(dataUrl)}
        onCancel={() => setSigning(false)}
      />

      {error && (
        <div className="flex items-start gap-2 text-sm text-red-300">
          <AlertTriangle size={14} className="text-red-500 shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={commit}
          disabled={busy || epcr.locked}
          className="rounded-md bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-700 px-3 py-2 text-sm font-semibold text-white min-h-touch"
        >
          {busy ? 'Recording…' : 'Record signature'}
        </button>
        <button
          type="button"
          onClick={() => { setSigning(false); setError(null); setPendingPng(null); }}
          className="rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-700 min-h-touch"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function SignedView({ row }: { row: SignatureRow }) {
  return (
    <div className="border border-emerald-700 bg-emerald-950/30 rounded p-3 flex items-start gap-3">
      <Check size={18} className="text-emerald-400 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-semibold text-emerald-200">
            {ROLE_META[row.role].label}
          </span>
          <span className="text-[11px] text-emerald-400">
            {new Date(row.signed_at).toLocaleString()}
          </span>
        </div>
        <div className="text-sm text-slate-200 mt-0.5">
          <strong>{row.signed_name}</strong>
          {row.signed_title && <span className="text-slate-400">, {row.signed_title}</span>}
        </div>
        <p className="text-[11px] text-slate-400 mt-1 italic leading-snug">
          &ldquo;{row.attestation}&rdquo;
        </p>
        {row.signature_png && (
          <img
            src={row.signature_png}
            alt={`${row.signed_name} signature`}
            className="mt-2 max-h-20 bg-white rounded border border-emerald-800 inline-block"
          />
        )}
        <div className="flex items-center gap-1.5 mt-1 text-[10px] font-mono text-emerald-500">
          <ShieldCheck size={10} />
          tamper hash {row.signature_hash.slice(0, 12)}…
        </div>
      </div>
    </div>
  );
}

